import { zodResolver } from '@hookform/resolvers/zod';
import type { Child, TimelineItem } from '@baby-tracker/contracts';
import {
    activityLabelSchema,
    gramsToMass,
    massToGrams,
    millilitresToVolume,
    nappyTypeSchema,
    sleepKindSchema,
    type MassDisplayUnit,
    type VolumeDisplayUnit,
    volumeToMillilitres,
} from '@baby-tracker/validation';
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
    useDeleteFeeding,
    useDeleteNappy,
    useDeletePumping,
    useDeleteSleep,
    useUpdateFeeding,
    useUpdateNappy,
    useUpdatePumping,
    useUpdateSleep,
} from '../../../hooks/useTracking.js';
import { formatWhen, timelineLabel } from '../../../lib/activity-format.js';
import { Button } from '../../ui/Button.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SelectField } from '../../ui/SelectField.js';
import { TextField } from '../../ui/TextField.js';
import styles from './tracking.module.css';

const amountField = z
    .string()
    .trim()
    .min(1, 'Enter an amount')
    .refine((value) => Number(value) > 0, 'Enter a number greater than 0');

export function HistoryList({
    familyId,
    items,
    children,
    volumeUnit,
    massUnit,
    timeZone,
    canLog,
}: {
    familyId: string;
    items: TimelineItem[];
    children: Child[];
    volumeUnit: VolumeDisplayUnit;
    massUnit: MassDisplayUnit;
    timeZone: string;
    canLog: boolean;
}): ReactElement {
    const names = new Map(children.map((child) => [child.id, child.name]));
    return (
        <ul className={styles.list}>
            {items.map((item) => (
                <HistoryItem
                    key={itemKey(item)}
                    familyId={familyId}
                    item={item}
                    childName={childName(item, names)}
                    volumeUnit={volumeUnit}
                    massUnit={massUnit}
                    timeZone={timeZone}
                    canLog={canLog}
                />
            ))}
        </ul>
    );
}

function itemKey(item: TimelineItem): string {
    switch (item.type) {
        case 'feeding':
            return `feeding-${item.feeding.id}`;
        case 'pumping':
            return `pumping-${item.pumping.id}`;
        case 'sleep':
            return `sleep-${item.sleep.id}`;
        case 'nappy':
            return `nappy-${item.nappy.id}`;
    }
}

function childName(item: TimelineItem, names: Map<string, string>): string | null {
    if (item.type === 'pumping') {
        return item.pumping.childId ? (names.get(item.pumping.childId) ?? null) : null;
    }
    const childId =
        item.type === 'feeding'
            ? item.feeding.childId
            : item.type === 'sleep'
              ? item.sleep.childId
              : item.nappy.childId;
    return names.get(childId) ?? null;
}

function HistoryItem({
    familyId,
    item,
    childName: name,
    volumeUnit,
    massUnit,
    timeZone,
    canLog,
}: {
    familyId: string;
    item: TimelineItem;
    childName: string | null;
    volumeUnit: VolumeDisplayUnit;
    massUnit: MassDisplayUnit;
    timeZone: string;
    canLog: boolean;
}): ReactElement {
    const [editing, setEditing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const deleteFeeding = useDeleteFeeding(familyId);
    const deletePumping = useDeletePumping(familyId);
    const deleteSleep = useDeleteSleep(familyId);
    const deleteNappy = useDeleteNappy(familyId);
    const { title, detail } = timelineLabel(item, volumeUnit);
    const who = name ? `${name} · ` : '';
    const pending =
        deleteFeeding.isPending ||
        deletePumping.isPending ||
        deleteSleep.isPending ||
        deleteNappy.isPending;

    async function remove(): Promise<void> {
        setActionError(null);
        try {
            if (item.type === 'feeding') await deleteFeeding.mutateAsync(item.feeding.id);
            else if (item.type === 'pumping') await deletePumping.mutateAsync(item.pumping.id);
            else if (item.type === 'sleep') await deleteSleep.mutateAsync(item.sleep.id);
            else await deleteNappy.mutateAsync(item.nappy.id);
        } catch (error) {
            setActionError(errorText(error));
        }
    }

    return (
        <li className={styles.item}>
            <p>{title}</p>
            <p className={styles.meta}>
                {who}
                {formatWhen(item.at, timeZone)} · {detail}
            </p>
            {canLog ? (
                <div className={fieldStyles.row}>
                    {editable(item) ? (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setEditing((open) => !open);
                                setConfirming(false);
                            }}
                        >
                            Edit
                        </Button>
                    ) : null}
                    {confirming ? (
                        <Button variant="danger" loading={pending} onClick={() => void remove()}>
                            Confirm delete
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setConfirming(true);
                                setEditing(false);
                            }}
                        >
                            Delete
                        </Button>
                    )}
                </div>
            ) : null}
            {editing ? (
                <HistoryEditor
                    familyId={familyId}
                    item={item}
                    volumeUnit={volumeUnit}
                    massUnit={massUnit}
                    onDone={() => {
                        setEditing(false);
                    }}
                />
            ) : null}
            <FormError message={actionError} />
        </li>
    );
}

function editable(item: TimelineItem): boolean {
    if (
        item.type === 'feeding' &&
        item.feeding.method === 'breast' &&
        item.feeding.endAt === null
    ) {
        return false;
    }
    if (item.type === 'pumping' && item.pumping.endAt === null) {
        return false;
    }
    if (item.type === 'sleep' && item.sleep.endAt === null) {
        return false;
    }
    return item.type !== 'feeding' || item.feeding.method !== 'breast';
}

function HistoryEditor({
    familyId,
    item,
    volumeUnit,
    massUnit,
    onDone,
}: {
    familyId: string;
    item: TimelineItem;
    volumeUnit: VolumeDisplayUnit;
    massUnit: MassDisplayUnit;
    onDone: () => void;
}): ReactElement | null {
    const updateFeeding = useUpdateFeeding(familyId);
    const updatePumping = useUpdatePumping(familyId);
    const updateSleep = useUpdateSleep(familyId);
    const updateNappy = useUpdateNappy(familyId);
    const [actionError, setActionError] = useState<string | null>(null);

    async function save(task: () => Promise<unknown>): Promise<void> {
        setActionError(null);
        try {
            await task();
            onDone();
        } catch (error) {
            setActionError(errorText(error));
        }
    }

    if (item.type === 'feeding' && item.feeding.method === 'bottle') {
        return (
            <BottleEditor
                unit={volumeUnit}
                initialMl={item.feeding.volumeMl ?? 0}
                pending={updateFeeding.isPending}
                error={actionError}
                onSubmit={(volumeMl) =>
                    save(() =>
                        updateFeeding.mutateAsync({ id: item.feeding.id, body: { volumeMl } }),
                    )
                }
            />
        );
    }
    if (item.type === 'feeding' && item.feeding.method === 'solid') {
        return (
            <SolidEditor
                unit={massUnit}
                initialFood={item.feeding.foodDescription ?? ''}
                initialGrams={item.feeding.amountG}
                pending={updateFeeding.isPending}
                error={actionError}
                onSubmit={(body) =>
                    save(() => updateFeeding.mutateAsync({ id: item.feeding.id, body }))
                }
            />
        );
    }
    if (item.type === 'pumping' && item.pumping.endAt !== null) {
        return (
            <BottleEditor
                unit={volumeUnit}
                initialMl={item.pumping.totalMl ?? 0}
                pending={updatePumping.isPending}
                error={actionError}
                label="Total"
                submitLabel="Save pump"
                onSubmit={(totalMl) =>
                    save(() =>
                        updatePumping.mutateAsync({ id: item.pumping.id, body: { totalMl } }),
                    )
                }
            />
        );
    }
    if (item.type === 'sleep') {
        return (
            <KindEditor
                label="Sleep"
                options={[
                    { value: 'nap', label: 'Nap' },
                    { value: 'night', label: 'Night' },
                    { value: 'unspecified', label: 'Sleep' },
                ]}
                initial={item.sleep.kind}
                pending={updateSleep.isPending}
                error={actionError}
                submitLabel="Save sleep"
                onSubmit={(kind) =>
                    save(() =>
                        updateSleep.mutateAsync({
                            id: item.sleep.id,
                            body: { kind: sleepKindSchema.parse(kind) },
                        }),
                    )
                }
            />
        );
    }
    if (item.type === 'nappy') {
        return (
            <KindEditor
                label="Nappy"
                options={[
                    { value: 'wet', label: 'Wet' },
                    { value: 'dirty', label: 'Dirty' },
                    { value: 'mixed', label: 'Mixed' },
                    { value: 'dry', label: 'Dry' },
                ]}
                initial={item.nappy.kind}
                pending={updateNappy.isPending}
                error={actionError}
                submitLabel="Save nappy"
                onSubmit={(kind) =>
                    save(() =>
                        updateNappy.mutateAsync({
                            id: item.nappy.id,
                            body: { kind: nappyTypeSchema.parse(kind) },
                        }),
                    )
                }
            />
        );
    }
    return null;
}

function BottleEditor({
    unit,
    initialMl,
    pending,
    error,
    onSubmit,
    label = 'Amount',
    submitLabel = 'Save bottle',
}: {
    unit: VolumeDisplayUnit;
    initialMl: number;
    pending: boolean;
    error: string | null;
    onSubmit: (millilitres: number) => Promise<void>;
    label?: string;
    submitLabel?: string;
}): ReactElement {
    const form = useForm<{ amount: string }>({
        resolver: zodResolver(z.object({ amount: amountField })),
        defaultValues: { amount: String(millilitresToVolume(initialMl, unit)) },
    });
    return (
        <form
            className={fieldStyles.stack}
            noValidate
            onSubmit={(event) => {
                void form.handleSubmit((values) =>
                    onSubmit(volumeToMillilitres(Number(values.amount), unit)),
                )(event);
            }}
        >
            <TextField
                label={`${label} (${unit})`}
                inputMode="decimal"
                autoComplete="off"
                error={form.formState.errors.amount?.message}
                {...form.register('amount')}
            />
            <FormError message={error} />
            <Button type="submit" loading={pending}>
                {submitLabel}
            </Button>
        </form>
    );
}

function SolidEditor({
    unit,
    initialFood,
    initialGrams,
    pending,
    error,
    onSubmit,
}: {
    unit: MassDisplayUnit;
    initialFood: string;
    initialGrams: number | null;
    pending: boolean;
    error: string | null;
    onSubmit: (body: { foodDescription: string; amountG?: number }) => Promise<void>;
}): ReactElement {
    const form = useForm<{ foodDescription: string; amount: string }>({
        resolver: zodResolver(
            z.object({
                foodDescription: activityLabelSchema,
                amount: z.string().trim(),
            }),
        ),
        defaultValues: {
            foodDescription: initialFood,
            amount: initialGrams === null ? '' : String(gramsToMass(initialGrams, unit)),
        },
    });
    return (
        <form
            className={fieldStyles.stack}
            noValidate
            onSubmit={(event) => {
                void form.handleSubmit((values) => {
                    const trimmed = values.amount.trim();
                    if (trimmed.length === 0) {
                        return onSubmit({ foodDescription: values.foodDescription });
                    }
                    const parsed = Number(trimmed);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                        form.setError('amount', { message: 'Enter a number greater than 0' });
                        return undefined;
                    }
                    return onSubmit({
                        foodDescription: values.foodDescription,
                        amountG: massToGrams(parsed, unit),
                    });
                })(event);
            }}
        >
            <TextField
                label="Food"
                autoComplete="off"
                error={form.formState.errors.foodDescription?.message}
                {...form.register('foodDescription')}
            />
            <TextField
                label={`Amount (${unit}, optional)`}
                inputMode="decimal"
                autoComplete="off"
                error={form.formState.errors.amount?.message}
                {...form.register('amount')}
            />
            <FormError message={error} />
            <Button type="submit" loading={pending}>
                Save solid
            </Button>
        </form>
    );
}

function KindEditor({
    label,
    options,
    initial,
    pending,
    error,
    submitLabel,
    onSubmit,
}: {
    label: string;
    options: readonly { value: string; label: string }[];
    initial: string;
    pending: boolean;
    error: string | null;
    submitLabel: string;
    onSubmit: (kind: string) => Promise<void>;
}): ReactElement {
    const form = useForm<{ kind: string }>({
        defaultValues: { kind: initial },
    });
    return (
        <form
            className={fieldStyles.stack}
            noValidate
            onSubmit={(event) => {
                void form.handleSubmit((values) => onSubmit(values.kind))(event);
            }}
        >
            <SelectField label={label} options={options} {...form.register('kind')} />
            <FormError message={error} />
            <Button type="submit" loading={pending}>
                {submitLabel}
            </Button>
        </form>
    );
}
